import React, { createContext, useContext, useState, useCallback } from "react";

interface BackNavigationState {
  fallbackPath: string | null;
}

/** Asks before leaving when the page has unsaved edits, then runs `next`. */
export type LeaveGuard = (next: () => void) => void;

interface BackNavigationContextValue {
  state: BackNavigationState;
  register: (fallbackPath: string) => void;
  clear: () => void;
  /** The guard of the page's dirty form, or null when nothing is unsaved. */
  leaveGuard: LeaveGuard | null;
  /** Each dirty form registers under its own id and removes only its own entry. */
  registerLeaveGuard: (id: string, guard: LeaveGuard | null) => void;
}

export const BackNavigationContext =
  createContext<BackNavigationContextValue | null>(null);

export function BackNavigationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [state, setState] = useState<BackNavigationState>({
    fallbackPath: null,
  });

  const register = useCallback((fallbackPath: string) => {
    setState({ fallbackPath });
  }, []);

  const clear = useCallback(() => {
    setState({ fallbackPath: null });
  }, []);

  const [guards, setGuards] = useState<Map<string, LeaveGuard>>(() => new Map());
  const registerLeaveGuard = useCallback((id: string, guard: LeaveGuard | null) => {
    setGuards((previous) => {
      if (guard === null ? !previous.has(id) : previous.get(id) === guard) return previous;
      const next = new Map(previous);
      if (guard === null) next.delete(id);
      else next.set(id, guard);
      return next;
    });
  }, []);
  // The most recently registered guard answers; a page holds at most one dirty form at a time.
  const leaveGuard = guards.size > 0 ? Array.from(guards.values())[guards.size - 1] : null;

  const value = React.useMemo(
    () => ({ state, register, clear, leaveGuard, registerLeaveGuard }),
    [state, register, clear, leaveGuard, registerLeaveGuard],
  );

  return (
    <BackNavigationContext.Provider value={value}>
      {children}
    </BackNavigationContext.Provider>
  );
}

export function useBackNavigationContext() {
  const ctx = useContext(BackNavigationContext);
  if (!ctx)
    throw new Error(
      "useBackNavigationContext must be used within BackNavigationProvider",
    );
  return ctx;
}
